import type { EnergyAwareWeights } from '../types';
import { DEFAULT_ENERGY_AWARE_WEIGHTS } from './fitnessCalculator';

const ENERGY_AWARE_WEIGHT_PRECISION = 4;

function roundEnergyAwareWeight(value: number): number {
  return Number(value.toFixed(ENERGY_AWARE_WEIGHT_PRECISION));
}

export function clampEnergyAwareWeight(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return roundEnergyAwareWeight(Math.max(0, Math.min(1, value)));
}

export function normalizeEnergyAwareWeights(
  weights?: Partial<EnergyAwareWeights> | null
): EnergyAwareWeights {
  if (!weights) {
    return { ...DEFAULT_ENERGY_AWARE_WEIGHTS };
  }

  const fitness = clampEnergyAwareWeight(weights.fitness ?? DEFAULT_ENERGY_AWARE_WEIGHTS.fitness);
  const energy = clampEnergyAwareWeight(weights.energy ?? DEFAULT_ENERGY_AWARE_WEIGHTS.energy);
  const total = fitness + energy;

  if (total <= 0) {
    return { ...DEFAULT_ENERGY_AWARE_WEIGHTS };
  }

  const normalizedFitness = roundEnergyAwareWeight(fitness / total);

  return {
    fitness: normalizedFitness,
    energy: roundEnergyAwareWeight(1 - normalizedFitness),
  };
}

export function createComplementaryEnergyAwareWeights(
  changedField: 'fitness' | 'energy',
  nextValue: number
): EnergyAwareWeights {
  const clampedValue = clampEnergyAwareWeight(nextValue);
  const complementaryValue = roundEnergyAwareWeight(1 - clampedValue);

  return changedField === 'fitness'
    ? { fitness: clampedValue, energy: complementaryValue }
    : { fitness: complementaryValue, energy: clampedValue };
}
